import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Dropdown,
  Modal,
  Input,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  CreditCardOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import paths from "routes/paths";
import { profileService } from "services/profileService";
import { useAuth } from "contexts/AuthContext";
import useOrganizationLocationTypes from "hooks/olreadia/useBusinessLocationTypes";
import OrganizationDrawer from "./OrganizationDrawer";
import OrganizationDetailsDrawer from "./OrganizationDetailsDrawer";
import OrganizationLineDrawer from "./OrganizationLineDrawer";
import OrganizationLocationDetailsDrawer from "./OrganizationLocationDetailsDrawer";
import OrganizationUserDrawer from "./OrganizationUserDrawer";
import OrganizationUserDetailsDrawer from "./OrganizationUserDetailsDrawer";
import ServicesManagerPanel from "./ServicesManagerPanel";
import { formatEnumLabel } from "../constants";

const toMessage = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

const logAxiosError = (context, err) => {
  try {
    const status = err?.response?.status;
    const method = err?.config?.method;
    const url = err?.config?.url;
    const responseData = err?.response?.data;
    // Avoid logging headers (may contain auth tokens).
    // eslint-disable-next-line no-console
    console.groupCollapsed?.(`[${context}] request failed${status ? ` (${status})` : ""}`);
    // eslint-disable-next-line no-console
    console.log({ status, method, url, responseData, message: err?.message });
    // eslint-disable-next-line no-console
    console.groupEnd?.();
  } catch {
    // eslint-disable-next-line no-console
    console.error(`[${context}] request failed`, err);
  }
};

const statusColor = (status) => {
  if (!status) return "default";
  if (status === "ACTIVE" || status === "APPROVED") return "green";
  if (status === "DRAFT" || status === "PENDING_REVIEW") return "gold";
  if (status === "SUSPENDED" || status === "DECLINED") return "red";
  return "default";
};

export default function OrganizationDetailsPage() {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const actorId = user?.id || "";
  const { locationTypes, locationTypesLoading, fetchLocationTypes } =
    useOrganizationLocationTypes();

  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const [organizationDrawerOpen, setOrganizationDrawerOpen] = useState(false);
  const [organizationDetailsOpen, setOrganizationDetailsOpen] = useState(false);
  const [organizationDetailsLoading, setOrganizationDetailsLoading] = useState(false);
  const [lineDrawerOpen, setLineDrawerOpen] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [userDrawerOpen, setUserDrawerOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [activeTab, setActiveTab] = useState("lines");
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [suspendModalOpen, setSuspendModalOpen] = useState(false);
  const [suspensionReason, setSuspensionReason] = useState("");
  const [approvalLoading, setApprovalLoading] = useState(false);

  const [lines, setLines] = useState([]);
  const [linesPage, setLinesPage] = useState({ totalElements: 0 });
  const [linesPagination, setLinesPagination] = useState({
    current: 1,
    pageSize: 20,
  });
  const [linesLoading, setLinesLoading] = useState(false);
  const [locationDetailsOpen, setLocationDetailsOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const [users, setUsers] = useState([]);
  const [usersPage, setUsersPage] = useState({ totalElements: 0 });
  const [usersPagination, setUsersPagination] = useState({
    current: 1,
    pageSize: 20,
  });
  const [usersLoading, setUsersLoading] = useState(false);
  const [userDetailsOpen, setUserDetailsOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const linesInFlightRef = React.useRef(new Map());
  const usersInFlightRef = React.useRef(new Map());

  const loadOrganization = async () => {
    if (!organizationId) return null;
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const res = await profileService.organizations.get(organizationId);
      // eslint-disable-next-line no-console
      console.log("[organizations.get] payload", res);
      // eslint-disable-next-line no-console
      console.log("[organizations.get] token present?", Boolean(sessionStorage.getItem("authToken")));
      setOrganization(res);
      return res;
    } catch (err) {
      logAxiosError("organizations.get", err);
      const status = err?.response?.status;
      if (status === 404) {
        setNotFound(true);
        setError("Organization not found.");
        setOrganization(null);
        return null;
      }

      const message = toMessage(err, "Failed to load organization");
      setError(message);
      setOrganization(null);
      toast.error(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const openOrganizationDetails = async () => {
    if (!organizationId) return;
    setOrganizationDetailsOpen(true);
    setOrganizationDetailsLoading(true);
    try {
      const fresh = await profileService.organizations.get(organizationId);
      setOrganization(fresh);
    } catch (err) {
      toast.error(toMessage(err, "Failed to load organization details"));
    } finally {
      setOrganizationDetailsLoading(false);
    }
  };

  const loadLines = async (pag = linesPagination) => {
    if (!organizationId) return;
    const key = `${organizationId}:${pag.current}:${pag.pageSize}`;
    const existing = linesInFlightRef.current.get(key);
    if (existing) return existing;
    setLinesLoading(true);
    const promise = (async () => {
      const res = await profileService.organizations.locations.list(
        organizationId,
        { page: pag.current - 1, size: pag.pageSize },
      );
      const content = Array.isArray(res?.content) ? res.content : [];
      setLines(content);
      setLinesPage({ totalElements: res?.totalElements ?? content.length });
      // eslint-disable-next-line no-console
      console.debug("[organizations.locations.list] sample", content?.[0]);
    })()
      .catch((err) => {
        toast.error(toMessage(err, "Failed to load organization locations"));
        throw err;
      })
      .finally(() => {
        linesInFlightRef.current.delete(key);
        setLinesLoading(false);
      });
    linesInFlightRef.current.set(key, promise);
    return promise;
  };

  const loadUsers = async (pag = usersPagination) => {
    if (!organizationId) return;
    const key = `${organizationId}:${pag.current}:${pag.pageSize}`;
    const existing = usersInFlightRef.current.get(key);
    if (existing) return existing;
    setUsersLoading(true);
    const promise = (async () => {
      const res = await profileService.organizations.users.list(
        organizationId,
        { page: pag.current - 1, size: pag.pageSize },
      );
      const content = Array.isArray(res?.content) ? res.content : [];
      setUsers(content);
      setUsersPage({ totalElements: res?.totalElements ?? content.length });
    })()
      .catch((err) => {
        toast.error(toMessage(err, "Failed to load organization users"));
        throw err;
      })
      .finally(() => {
        usersInFlightRef.current.delete(key);
        setUsersLoading(false);
      });
    usersInFlightRef.current.set(key, promise);
    return promise;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!organizationId || organizationId === "undefined" || organizationId === "null") {
        setOrganization(null);
        setError("Invalid organization id in URL.");
        return;
      }
      fetchLocationTypes();
      const loaded = await loadOrganization();
      if (cancelled) return;
      if (loaded) {
        setLinesLoading(true);
        setUsersLoading(true);
        await Promise.all([loadLines(), loadUsers()]);
      } else {
        setLinesLoading(false);
        setUsersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    }, [organizationId]);

  const handleOrganizationSave = async (payload) => {
    setLoading(true);
    try {
      await profileService.organizations.update(organizationId, payload, actorId);
      toast.success("Organization updated");
      setOrganizationDrawerOpen(false);
      await loadOrganization();
    } catch (err) {
      toast.error(toMessage(err, "Failed to update organization"));
    } finally {
      setLoading(false);
    }
  };

  const handleOrganizationRemove = () => {
    Modal.confirm({
      title: "Remove organization?",
      content: "This will deactivate/remove the organization.",
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async () => {
        setLoading(true);
        try {
          await profileService.organizations.delete(organizationId, actorId);
          toast.success("Organization removed");
          navigate(paths.organizations);
        } catch (err) {
          toast.error(toMessage(err, "Failed to remove organization"));
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const updateApproval = async (status, reason) => {
    setApprovalLoading(true);
    try {
      await profileService.organizations.update(
        organizationId,
        {
          status,
          ...(status === "DECLINED" && reason
            ? { declineReason: reason.trim() }
            : {}),
          ...(status === "SUSPENDED" && reason
            ? { suspensionReason: reason.trim() }
            : {}),
        },
        actorId,
      );
      const messages = {
        APPROVED: "Organization approved",
        DECLINED: "Organization declined",
        SUSPENDED: "Organization suspended",
        PENDING_REVIEW: "Organization moved to pending review",
      };
      toast.success(messages[status] || "Organization status updated");
      setDeclineModalOpen(false);
      setDeclineReason("");
      setSuspendModalOpen(false);
      setSuspensionReason("");
      await loadOrganization();
    } catch (err) {
      toast.error(toMessage(err, "Failed to update organization status"));
    } finally {
      setApprovalLoading(false);
    }
  };

  const confirmApproval = () => {
    Modal.confirm({
      title: "Approve organization?",
      content: `Are you sure you want to approve ${organization?.name || "this organization"}?`,
      okText: "Yes, approve",
      cancelText: "No",
      zIndex: 2600,
      onOk: () => updateApproval("APPROVED"),
    });
  };

  const confirmStatusChange = (status, title, content, okText, danger = false) => {
    Modal.confirm({
      title,
      content,
      okText,
      cancelText: "Cancel",
      okButtonProps: { danger },
      zIndex: 2600,
      onOk: () => updateApproval(status),
    });
  };

  const handleLineSubmit = async (payload) => {
    setLoading(true);
    try {
      // eslint-disable-next-line no-console
      console.debug("[organizationLocations.submit] payload", payload);
      if (editingLine?.id) {
        await profileService.organizations.locations.update(
          organizationId,
          editingLine.id,
          payload,
          actorId,
        );
        toast.success("Organization location updated");
      } else {
        await profileService.organizations.locations.create(
          organizationId,
          payload,
          actorId,
        );
        toast.success("Organization location created");
      }
      setLineDrawerOpen(false);
      setEditingLine(null);
      await loadLines();
      await loadOrganization();
    } catch (err) {
      toast.error(toMessage(err, "Failed to save organization location"));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleLineRemove = (record) => {
    Modal.confirm({
      title: "Remove organization location?",
      content: "This will deactivate/remove the organization location.",
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async () => {
        setLoading(true);
        try {
          await profileService.organizations.locations.delete(
            organizationId,
            record.id,
            actorId,
          );
          toast.success("Organization location removed");
          await loadLines();
          await loadOrganization();
        } catch (err) {
          toast.error(toMessage(err, "Failed to remove organization location"));
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleUserSubmit = async (payload) => {
    setLoading(true);
    try {
      if (editingUser?.id) {
        await profileService.organizations.users.update(
          organizationId,
          editingUser.userId,
          {
            role: payload.role,
            status: payload.status,
            primaryContact: payload.primaryContact,
          },
          actorId,
        );
        toast.success("Organization user updated");
      } else {
        await profileService.organizations.users.add(
          organizationId,
          payload,
          actorId,
        );
        toast.success("Organization user added");
      }
      setUserDrawerOpen(false);
      setEditingUser(null);
      await loadUsers();
      await loadOrganization();
    } catch (err) {
      toast.error(toMessage(err, "Failed to save organization user"));
    } finally {
      setLoading(false);
    }
  };

  const handleUserRemove = (record) => {
    Modal.confirm({
      title: "Remove organization user?",
      content: "This will remove the user from the organization.",
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async () => {
        setLoading(true);
        try {
          await profileService.organizations.users.remove(
            organizationId,
            record.userId,
            actorId,
          );
          toast.success("Organization user removed");
          await loadUsers();
          await loadOrganization();
        } catch (err) {
          toast.error(toMessage(err, "Failed to remove organization user"));
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const openLocationDetails = (record) => {
    setSelectedLocation(record);
    setLocationDetailsOpen(true);
  };

  const openUserDetails = (record) => {
    setSelectedUser(record);
    setUserDetailsOpen(true);
  };

  const lineColumns = [
    { title: "Name", dataIndex: "name", key: "name", ellipsis: true },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (val) => <Tag color={statusColor(val)}>{formatEnumLabel(val)}</Tag>,
    },
    {
      title: "Location Type",
      key: "type",
      render: (_, r) => r?.organizationLocationType?.label || r?.organizationLocationType?.code || "-",
    },
    {
      title: "Location",
      key: "location",
      render: (_, r) => {
        const address1 = r?.locationInformation?.addressLine1;
        const city = r?.locationInformation?.city;
        const country = r?.locationInformation?.country;
        const primary = [city, country].filter(Boolean).join(", ");
        return address1 || primary || "-";
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="be-actions" onClick={(e) => e.stopPropagation()}>
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "edit",
                  label: "Edit",
                  icon: <EditOutlined />,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    setEditingLine(record);
                    setLineDrawerOpen(true);
                  },
                },
                {
                  key: "remove",
                  label: "Remove",
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    handleLineRemove(record);
                  },
                },
              ],
            }}
          >
            <Button size="small" type="text" aria-label="Actions">
              <EllipsisOutlined style={{ fontSize: 18 }} />
            </Button>
          </Dropdown>
        </div>
      ),
    },
  ];

  const userColumns = [
    {
      title: "Name",
      key: "name",
      render: (_, r) => {
        const name = [r.firstName, r.lastName].filter(Boolean).join(" ");
        return name || "-";
      },
    },
    { title: "Email", dataIndex: "email", key: "email", ellipsis: true },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (val) => formatEnumLabel(val) || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (val) => <Tag color={statusColor(val)}>{formatEnumLabel(val)}</Tag>,
    },
    {
      title: "Primary",
      dataIndex: "primaryContact",
      key: "primaryContact",
      render: (val) => <Tag color={val ? "green" : "default"}>{val ? "Yes" : "No"}</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="be-actions" onClick={(e) => e.stopPropagation()}>
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "edit",
                  label: "Edit",
                  icon: <EditOutlined />,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    setEditingUser({
                      id: record.id,
                      userId: record.userId,
                      email: record.email,
                      role: record.role,
                      status: record.status,
                      primaryContact: record.primaryContact,
                    });
                    setUserDrawerOpen(true);
                  },
                },
                {
                  key: "remove",
                  label: "Remove",
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    handleUserRemove(record);
                  },
                },
              ],
            }}
          >
            <Button size="small" type="text" aria-label="Actions">
              <EllipsisOutlined style={{ fontSize: 18 }} />
            </Button>
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2 mt-2 gap-3">
        <WidgetSectionTitle
          icon="building"
          title={organization?.name || "Organization"}
          subtitle= "Manage organization locations, attached services, and linked users."  
          className="mb-2 mt-2 flex-1"
        />
        <Space>
        {["ACTIVE", "DRAFT", "PENDING_REVIEW", "SUSPENDED"].includes(
          organization?.status,
        ) ? (
          <>
            <Button
              size="small"
              type="primary"
              disabled={!organization?.id || loading || approvalLoading}
              loading={approvalLoading}
              onClick={confirmApproval}
            >
              Approve
            </Button>
            {organization?.status !== "SUSPENDED" ? (
              <Button
                size="small"
                danger
                disabled={!organization?.id || loading || approvalLoading}
                loading={approvalLoading}
                onClick={() => setDeclineModalOpen(true)}
              >
                Decline
              </Button>
            ) : null}
          </>
        ) : null}
        {organization?.status === "APPROVED" ? (
          <Button
            size="small"
            danger
            disabled={!organization?.id || loading || approvalLoading}
            loading={approvalLoading}
            onClick={() => setSuspendModalOpen(true)}
          >
            Suspend
          </Button>
        ) : null}
        {organization?.status === "DECLINED" ? (
          <Button
            size="small"
            disabled={!organization?.id || loading || approvalLoading}
            loading={approvalLoading}
            onClick={() =>
              confirmStatusChange(
                "PENDING_REVIEW",
                "Move to pending review?",
                `Move ${organization?.name || "this organization"} back to pending review?`,
                "Move to review",
              )
            }
          >
            Pending Review
          </Button>
        ) : null}
        <Tooltip title="View organization details">
          <Button
            icon={<InfoCircleOutlined />}
            disabled={!organization?.id || loading}
            onClick={openOrganizationDetails}
          >
            Details
          </Button>
        </Tooltip>
        <Button
          disabled={!organizationId || loading}
          onClick={() => navigate(`${paths.tasks}?organizationId=${organizationId}`)}
        >
          Tasks
        </Button>
        <Button
          icon={<CreditCardOutlined />}
          disabled={!organizationId || loading}
          onClick={() =>
            navigate(
              `${paths.subscriptions}?tab=subscriptions&subscriberType=BUSINESS&subscriberId=${encodeURIComponent(
                organization?.id || organizationId,
              )}`,
            )
          }
        >
          Subscriptions
        </Button>
        <Button type="primary" onClick={() => navigate(paths.organizations)}>
          Go Back
        </Button>
      </Space>
      </div>

      <Modal
        title={`Decline ${organization?.name || "organization"}?`}
        open={declineModalOpen}
        zIndex={2600}
        okText="Decline"
        cancelText="Cancel"
        okButtonProps={{ danger: true, disabled: !declineReason.trim() }}
        cancelButtonProps={{ disabled: approvalLoading }}
        confirmLoading={approvalLoading}
        onOk={() => updateApproval("DECLINED", declineReason)}
        onCancel={() => {
          setDeclineModalOpen(false);
          setDeclineReason("");
        }}
      >
        <p style={{ marginBottom: 12 }}>
          Please provide a reason. This will be saved with the declined organization.
        </p>
        <Input.TextArea
          value={declineReason}
          onChange={(event) => setDeclineReason(event.target.value)}
          placeholder="Enter the reason for declining this organization"
          rows={4}
          maxLength={2000}
          showCount
          style={{ marginBottom: 24 }}
        />
      </Modal>

      <Modal
        title={`Suspend ${organization?.name || "organization"}?`}
        open={suspendModalOpen}
        zIndex={2600}
        okText="Suspend"
        cancelText="Cancel"
        okButtonProps={{ danger: true, disabled: !suspensionReason.trim() }}
        cancelButtonProps={{ disabled: approvalLoading }}
        confirmLoading={approvalLoading}
        onOk={() => updateApproval("SUSPENDED", suspensionReason)}
        onCancel={() => {
          setSuspendModalOpen(false);
          setSuspensionReason("");
        }}
      >
        <p style={{ marginBottom: 12 }}>
          Please provide a reason. This will be saved with the suspended organization.
        </p>
        <Input.TextArea
          value={suspensionReason}
          onChange={(event) => setSuspensionReason(event.target.value)}
          placeholder="Enter the reason for suspending this organization"
          rows={4}
          maxLength={2000}
          showCount
          style={{ marginBottom: 24 }}
        />
      </Modal>

      {error ? (
        <Alert
          type={notFound ? "warning" : "error"}
          showIcon
          message={notFound ? "Not found" : "Failed to load"}
          description={error}
          style={{ marginBottom: 12 }}
          action={
            <Space>
              <Button size="small" onClick={() => navigate(paths.organizations)}>
                Back to list
              </Button>
              <Button size="small" onClick={loadOrganization}>
                Retry
              </Button>
            </Space>
          }
        />
      ) : null}

      {error && !organization ? null : (
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "lines",
            label: "Organization Locations",
            children: (
              <div>
                <div
                  className="d-flex justify-content-end"
                  style={{ marginBottom: 12 }}
                >
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    disabled={!organization}
                    onClick={() => {
                      setEditingLine(null);
                      setLineDrawerOpen(true);
                    }}
                  >
                    New Location
                  </Button>
                </div>
                <Table
                  rowKey="id"
                  loading={loading || linesLoading}
                  columns={lineColumns}
                  dataSource={lines}
                  className="custom-table"
                  bordered
                  tableLayout="auto"
                  scroll={{ x: "max-content" }}
                  size="small"
                  pagination={{
                    ...linesPagination,
                    total: linesPage.totalElements,
                    showSizeChanger: true,
                  }}
                  onChange={(pag) => {
                    const next = { current: pag.current, pageSize: pag.pageSize };
                    setLinesPagination(next);
                    loadLines(next);
                  }}
                  onRow={(record) => ({
                    onClick: (e) => {
                      if (e?.target?.closest?.(".be-actions")) return;
                      openLocationDetails(record);
                    },
                  })}
                />
              </div>
            ),
          },
          {
            key: "services",
            label: "Organization Offerings",
            children: (
              <div>
                <ServicesManagerPanel
                  organizationId={organizationId}
                />
              </div>
            ),
          },
          {
            key: "users",
            label: (
              <span>
                <TeamOutlined /> Organization Users
              </span>
            ),
            children: (
              <div>
                <div
                  className="d-flex justify-content-end"
                  style={{ marginBottom: 12 }}
                >
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    disabled={!organization}
                    onClick={() => {
                      setEditingUser(null);
                      setUserDrawerOpen(true);
                    }}
                  >
                    Add User
                  </Button>
                </div>
                <Table
                  rowKey="id"
                  loading={loading || usersLoading}
                  columns={userColumns}
                  dataSource={users}
                  className="custom-table"
                  bordered
                  tableLayout="auto"
                  scroll={{ x: "max-content" }}
                  size="small"
                  pagination={{
                    ...usersPagination,
                    total: usersPage.totalElements,
                    showSizeChanger: true,
                  }}
                  onChange={(pag) => {
                    const next = { current: pag.current, pageSize: pag.pageSize };
                    setUsersPagination(next);
                    loadUsers(next);
                  }}
                  onRow={(record) => ({
                    onClick: (e) => {
                      if (e?.target?.closest?.(".be-actions")) return;
                      openUserDetails(record);
                    },
                  })}
                />
              </div>
            ),
          },
        ]}
      />
      )}

      <OrganizationDrawer
        open={organizationDrawerOpen}
        onClose={() => setOrganizationDrawerOpen(false)}
        onSubmit={handleOrganizationSave}
        loading={loading}
        initialValues={organization}
        locationTypes={locationTypes}
        locationTypesLoading={locationTypesLoading}
      />

      <OrganizationLineDrawer
        open={lineDrawerOpen}
        onClose={() => {
          setLineDrawerOpen(false);
          setEditingLine(null);
        }}
        onSubmit={handleLineSubmit}
        loading={loading}
        initialValues={editingLine}
        locationTypes={locationTypes}
        locationTypesLoading={locationTypesLoading}
      />

      <OrganizationLocationDetailsDrawer
        open={locationDetailsOpen}
        location={selectedLocation}
        onClose={() => setLocationDetailsOpen(false)}
        onEdit={(record) => {
          setLocationDetailsOpen(false);
          setEditingLine(record);
          setLineDrawerOpen(true);
        }}
      />

      <OrganizationUserDrawer
        open={userDrawerOpen}
        onClose={() => {
          setUserDrawerOpen(false);
          setEditingUser(null);
        }}
        onSubmit={handleUserSubmit}
        loading={loading}
        initialValues={editingUser}
      />

      <OrganizationUserDetailsDrawer
        open={userDetailsOpen}
        user={selectedUser}
        onClose={() => setUserDetailsOpen(false)}
        onEdit={(record) => {
          setUserDetailsOpen(false);
          setEditingUser({
            id: record?.id,
            userId: record?.userId,
            email: record?.email,
            role: record?.role,
            status: record?.status,
            primaryContact: record?.primaryContact,
          });
          setUserDrawerOpen(true);
        }}
      />

      <OrganizationDetailsDrawer
        open={organizationDetailsOpen}
        entity={organization}
        loading={organizationDetailsLoading}
        onClose={() => setOrganizationDetailsOpen(false)}
        onEdit={() => {
          setOrganizationDetailsOpen(false);
          setOrganizationDrawerOpen(true);
        }}
      />
    </div>
  );
}
